import { useCallback, useEffect, useMemo, useState } from "react";
import { ICandidate } from "../../Typings/Candidate";

export interface UseCandidateSelectionOptions {
  /**
   * Total number of candidates available for selection (after filters applied)
   */
  totalAvailableCount?: number;
  /**
   * IDs of candidates that are already included/unavailable (e.g., already in a group)
   */
  alreadyIncludedIds?: string[];
  /**
   * Whether to automatically clear selections when filters change and no candidates are available
   */
  autoClearOnFilterChange?: boolean;
  /**
   * Dependency that represents current filter state (e.g., filterQueryString)
   * Used to detect filter changes for auto-clearing
   */
  filterDependency?: string;
}

export interface CandidateSelectionState {
  getters: {
    selectedCandidates: ICandidate[];
    selectedCandidateIds: string[];
    selectedCount: number;
    selectAll: boolean;
    excludedCandidateIds: string[];
    disableSelectAll: boolean;
    isCandidateSelected: (candidateId: string) => boolean;
    isRowDisabled: (candidateId: string) => boolean;
    currentPageSelectedIds: string[];
    getSelectionCountAfter: (candidateId: string, select: boolean) => number;
  };
  setters: {
    setSelectedCandidates: (candidates: ICandidate[]) => void;
    setSelectedCandidateIds: (ids: string[]) => void;
  };
  handlers: {
    handleSelectRow: (row: ICandidate, select: boolean) => void;
    handleSelectAllClick: (selected: boolean, currentPageCandidates?: ICandidate[]) => void;
    handleClearSelected: () => void;
  };
}

/**
 * Unified hook for managing candidate selection with Select All pattern.
 * Supports both individual selection and "select all with exclusions" mode.
 *
 * @example
 * // Basic usage for ManageCandidates
 * const selection = useCandidateSelection({
 *   totalAvailableCount: candidates?.totalItems
 * });
 *
 * @example
 * // Advanced usage with already included IDs (for Add to Group)
 * const selection = useCandidateSelection({
 *   totalAvailableCount: totalItems,
 *   alreadyIncludedIds: groupMemberIds,
 *   autoClearOnFilterChange: true,
 *   filterDependency: filterQueryString
 * });
 */
export function useCandidateSelection(options: UseCandidateSelectionOptions = {}): CandidateSelectionState {
  const {
    totalAvailableCount = 0,
    alreadyIncludedIds = [],
    autoClearOnFilterChange = false,
    filterDependency = "",
  } = options;

  const [selectedCandidatesMap, setSelectedCandidatesMap] = useState<Map<string, ICandidate>>(new Map());
  const [excludedCandidateIds, setExcludedCandidateIds] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const alreadyIncludedIdsSet = useMemo(() => new Set(alreadyIncludedIds), [alreadyIncludedIds]);
  const selectedCandidates = useMemo(
    () => Array.from(selectedCandidatesMap.values()),
    [selectedCandidatesMap],
  );

  const selectedCandidateIds = useMemo(
    () => Array.from(selectedCandidatesMap.keys()),
    [selectedCandidatesMap],
  );

  // Track IDs of candidates selected on the current page (from selectedCandidatesMap)
  // This is separate from selectedCount which can be the full filtered count when selectAll=true
  const currentPageSelectedIds = useMemo(
    () => Array.from(selectedCandidatesMap.keys()),
    [selectedCandidatesMap],
  );

  const selectedCount = useMemo(() => {
    if (selectAll && totalAvailableCount > 0) {
      return totalAvailableCount - excludedCandidateIds.length - alreadyIncludedIdsSet.size;
    }
    return selectedCandidates.length;
  }, [
    selectAll,
    totalAvailableCount,
    excludedCandidateIds.length,
    alreadyIncludedIdsSet.size,
    selectedCandidates.length,
  ]);

  const disableSelectAll = useMemo(() => {
    const availableCount =
      totalAvailableCount > 0
        ? totalAvailableCount - excludedCandidateIds.length - alreadyIncludedIdsSet.size
        : 0;
    return availableCount === 0;
  }, [totalAvailableCount, excludedCandidateIds.length, alreadyIncludedIdsSet.size]);

  const isCandidateSelected = useCallback(
    (candidateId: string) => {
      if (alreadyIncludedIdsSet.has(candidateId)) {
        return false;
      }
      if (selectAll) {
        return !excludedCandidateIds.includes(candidateId);
      }
      return selectedCandidatesMap.has(candidateId);
    },
    [selectAll, excludedCandidateIds, selectedCandidatesMap, alreadyIncludedIdsSet],
  );

  const isRowDisabled = useCallback(
    (candidateId: string) => alreadyIncludedIdsSet.has(candidateId),
    [alreadyIncludedIdsSet],
  );

  const getSelectionCountAfter = useCallback(
    (candidateId: string, select: boolean): number => {
      if (alreadyIncludedIdsSet.has(candidateId)) {
        return selectedCount; // No change for disabled rows
      }

      let predictedCount: number;

      if (selectAll) {
        if (select) {
          // Selecting in selectAll mode: remove from exclusions
          const isCurrentlyExcluded = excludedCandidateIds.includes(candidateId);
          predictedCount = isCurrentlyExcluded ? selectedCount + 1 : selectedCount;
        } else {
          // Deselecting in selectAll mode: add to exclusions
          const isCurrentlyExcluded = excludedCandidateIds.includes(candidateId);
          predictedCount = isCurrentlyExcluded ? selectedCount : selectedCount - 1;
        }
      } else {
        if (select) {
          // Adding to selection
          predictedCount = selectedCandidatesMap.has(candidateId) ? selectedCount : selectedCount + 1;
        } else {
          // Removing from selection
          predictedCount = selectedCandidatesMap.has(candidateId) ? selectedCount - 1 : selectedCount;
        }
      }

      return predictedCount;
    },
    [alreadyIncludedIdsSet, selectAll, excludedCandidateIds, selectedCount, selectedCandidatesMap],
  );

  const handleSelectRow = useCallback(
    (row: ICandidate, select: boolean) => {
      if (alreadyIncludedIdsSet.has(row.id)) {
        return; // Can't select disabled rows
      }

      if (select) {
        setSelectedCandidatesMap((prev) => {
          const next = new Map(prev);
          next.set(row.id, row);
          return next;
        });
        if (selectAll) {
          // Remove from excluded list when selecting in selectAll mode
          setExcludedCandidateIds((prev) => prev.filter((id) => id !== row.id));
        }
      } else {
        setSelectedCandidatesMap((prev) => {
          const next = new Map(prev);
          next.delete(row.id);
          return next;
        });
        if (selectAll) {
          // Add to excluded list when deselecting in selectAll mode
          setExcludedCandidateIds((prev) => [...prev, row.id]);
        }
      }
    },
    [
      selectAll,
      alreadyIncludedIdsSet,
      selectedCandidatesMap.size,
      totalAvailableCount,
      excludedCandidateIds.length,
    ],
  );

  const handleClearSelected = useCallback(() => {
    setSelectedCandidatesMap(new Map());
    setExcludedCandidateIds([]);
    setSelectAll(false);
  }, []);

  const handleSelectAllClick = useCallback(
    (selected: boolean, currentPageCandidates?: ICandidate[]) => {
      if (selected) {
        // When selecting all, mark the flag and clear exclusions
        setSelectAll(true);
        setExcludedCandidateIds([]);
        // Add current page candidates to the map for immediate data availability
        if (currentPageCandidates) {
          setSelectedCandidatesMap((prev) => {
            const next = new Map(prev);
            currentPageCandidates.forEach((c) => next.set(c.id, c));
            return next;
          });
        }
      } else {
        handleClearSelected();
      }
    },
    [handleClearSelected],
  );

  const setSelectedCandidates = useCallback((candidates: ICandidate[]) => {
    setSelectedCandidatesMap(new Map(candidates.map((c) => [c.id, c])));
    setSelectAll(false);
    setExcludedCandidateIds([]);
  }, []);

  const setSelectedCandidateIds = useCallback((ids: string[]) => {
    // Create map with just IDs, full candidate objects may not be available
    setSelectedCandidatesMap(new Map(ids.map((id) => [id, { id } as ICandidate])));
    setSelectAll(false);
    setExcludedCandidateIds([]);
  }, []);

  // Auto-clear when filters change and no candidates are available
  useEffect(() => {
    if (autoClearOnFilterChange && disableSelectAll) {
      handleClearSelected();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDependency, disableSelectAll, autoClearOnFilterChange]);

  return {
    getters: {
      selectedCandidates,
      selectedCandidateIds,
      selectedCount,
      selectAll,
      excludedCandidateIds,
      disableSelectAll,
      isCandidateSelected,
      isRowDisabled,
      currentPageSelectedIds,
      getSelectionCountAfter,
    },
    setters: {
      setSelectedCandidates,
      setSelectedCandidateIds,
    },
    handlers: {
      handleSelectRow,
      handleSelectAllClick,
      handleClearSelected,
    },
  };
}
